import { useRef, useState } from 'react';
import type { VatImportMode, VatImportPreview } from '@tosho/contracts';
import { PrototypeIcon } from './PrototypeIcon';
import { remunerationMoney } from './RemunerationColumn';
import { bundleVatPdfs, vatDeclarationsApi } from './vat-declarations-api';

/** Même parcours que le dépôt URSSAF, avec des PDF DGFiP multiples et aucune
 * dépendance à la connexion ou à la planification Freebe. La prévisualisation
 * porte les périodes réellement lues dans les pièces, pas leurs noms. */
export function VatDeclarationsCard({
  csrfToken,
  onImported,
}: {
  readonly csrfToken: string;
  readonly onImported?: () => void;
}): React.JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<readonly File[]>([]);
  const [mode, setMode] = useState<VatImportMode>('historical');
  const [preview, setPreview] = useState<VatImportPreview | null>(null);
  const [bundle, setBundle] = useState<Uint8Array | null>(null);
  const [pending, setPending] = useState<'preview' | 'import' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const valid =
    files.length > 0 &&
    files.length <= 60 &&
    files.every((f) => /\.pdf$/iu.test(f.name) && f.size > 0 && f.size <= 2_097_152) &&
    files.reduce((sum, f) => sum + f.size, 0) <= 15_000_000;
  function select(candidates: readonly File[]): void {
    setFiles(candidates);
    setPreview(null);
    setBundle(null);
    setResult(null);
    setError(null);
  }
  async function inspect(): Promise<void> {
    if (!valid || pending) return;
    setPending('preview');
    setError(null);
    setPreview(null);
    setResult(null);
    try {
      const bytes = await bundleVatPdfs(files);
      const value = await vatDeclarationsApi.preview(bytes, mode, csrfToken);
      setBundle(bytes);
      setPreview(value);
    } catch {
      setError(
        'Pièces non reconnues ou incohérentes. Utilisez les PDF CA3 déposés depuis le compte fiscal de votre organisation.',
      );
    } finally {
      setPending(null);
    }
  }
  async function apply(): Promise<void> {
    if (!preview || !bundle || pending) return;
    setPending('import');
    setError(null);
    try {
      const r = await vatDeclarationsApi.confirm(bundle, mode, csrfToken, preview.archiveSha256);
      setResult(
        `${r.imported} déclaration(s) ajoutée(s), ${r.unchanged} déjà connue(s). Montants déclarés raccordés aux finances ; le paiement reste distinct.`,
      );
      setPreview(null);
      setBundle(null);
      setFiles([]);
      if (input.current) input.current.value = '';
      onImported?.();
    } catch {
      setError(
        'L’import n’a pas abouti. Vous pouvez relancer le même lot ; les déclarations déjà présentes seront reconnues.',
      );
    } finally {
      setPending(null);
    }
  }
  return (
    <article
      id="vat-declarations-section"
      className="freebe-integration__dataset freebe-declarations"
    >
      <span className="freebe-integration__dataset-icon" aria-hidden="true">
        <PrototypeIcon name="file-text" />
      </span>
      <span className="freebe-integration__dataset-copy">
        <strong>Déclarations TVA</strong>
        <small>PDF CA3 du compte fiscal · impots.gouv.fr — pièces privées</small>
      </span>
      <span className="freebe-integration__dataset-state">PDF</span>
      <div className="freebe-declarations__content">
        <p>Les CA3 font foi pour les montants déclarés. Le paiement est suivi séparément.</p>
        <div className="freebe-declarations__modes">
          <span data-active={mode === 'historical'}>Historique</span>
          <button
            type="button"
            role="switch"
            aria-label="TVA : année courante uniquement"
            aria-checked={mode === 'current_year'}
            disabled={pending !== null}
            onClick={() => {
              setMode(mode === 'historical' ? 'current_year' : 'historical');
              setPreview(null);
              setBundle(null);
              setResult(null);
            }}
          >
            <span aria-hidden="true" />
          </button>
          <span data-active={mode === 'current_year'}>Année courante</span>
        </div>
        <div
          className="freebe-declarations__dropzone"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            if (!pending) select(Array.from(event.dataTransfer.files));
          }}
        >
          <input
            ref={input}
            id="vat-declarations-file"
            aria-label="PDF des déclarations TVA"
            type="file"
            accept=".pdf,application/pdf"
            multiple
            hidden
            disabled={pending !== null}
            onChange={(event) => select(Array.from(event.currentTarget.files ?? []))}
          />
          <button type="button" disabled={pending !== null} onClick={() => input.current?.click()}>
            Parcourir
          </button>
          <small>ou glisser-déposer les PDF ici · 2 Mio par pièce, 15 Mio par lot.</small>
        </div>
        {files.length > 0 ? (
          <small className="freebe-declarations__filename">{files.length} PDF sélectionné(s)</small>
        ) : null}
        {files.length > 0 && !valid ? (
          <p className="freebe-declarations__error tosho-feedback is-error" role="alert">
            Choisissez jusqu’à 60 PDF, dans les limites indiquées.
          </p>
        ) : null}
        <button type="button" disabled={!valid || pending !== null} onClick={() => void inspect()}>
          {pending === 'preview' ? 'Lecture des CA3…' : 'Prévisualiser l’import'}
        </button>
        {preview ? (
          <div className="freebe-declarations__preview" role="status">
            <strong>
              {preview.declarationCount} déclaration(s) lue(s) · SIREN {preview.siren}
            </strong>
            <span>
              {preview.duplicateCount} doublon(s) reconnu(s) · {preview.excludedOutsideCurrentYear}{' '}
              PDF hors année courante écarté(s).
            </span>
            <div className="freebe-integration__table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Trimestre</th>
                    <th>TVA nette déclarée</th>
                    <th>Crédit à reporter</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.declarations.map((row) => (
                    <tr key={row.id}>
                      <th scope="row">{row.period}</th>
                      <td>{remunerationMoney(row.netDueCents)}</td>
                      <td>{remunerationMoney(row.creditToCarryCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <small>
              La confirmation conserve ces pièces et leurs versions. Aucun règlement n’est déduit du
              dépôt.
            </small>
            <button
              type="button"
              disabled={pending !== null || preview.declarationCount === 0}
              onClick={() => void apply()}
            >
              {pending === 'import' ? 'Import en cours…' : 'Confirmer l’import des preuves'}
            </button>
          </div>
        ) : null}
        {error ? (
          <p className="freebe-declarations__error tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
        {result ? (
          <p className="freebe-declarations__result tosho-feedback is-success" role="status">
            {result}
          </p>
        ) : null}
        <small>
          Dépôt manuel des PDF téléchargés sur le site des impôts. Lecture et classement
          automatiques dans Tosho.
        </small>
      </div>
    </article>
  );
}
